import { PRODUCT_MENU, PRODUCT_SHOWCASE } from "@/lib/marketing-product-menu";

/** Client-side fallback when /api/public/landing is unavailable. Mirrors backend defaults. */
export const LANDING_DEFAULTS = {
  productMenu: PRODUCT_MENU,
  productShowcase: PRODUCT_SHOWCASE,
  nav: {
    links: [
      { label: "Pricing", href: "#pricing" },
      { label: "Solutions", href: "#event-types" },
      { label: "AivoryONE", href: "#aivoryone" },
      { label: "Compare", href: "/compare" },
      { label: "Customers", href: "#case-studies" },
      { label: "Why Aivory", href: "#why-aivory" },
    ],
    launchLabel: "Book a demo",
    trialLabel: "Start free trial",
    trialHref: "/signup",
    loginLabel: "Log in",
  },
  hero: {
    eyebrow: "Singapore-hosted omnichannel event management · From SGD 1,188/yr",
    title: "Registration, check-in, badges & AI planning",
    titleAccent: "in one event platform.",
    subtitle:
      "Aivory EMS unifies event registration software, QR check-in, badge printing, email campaigns, and AivoryONE AI co-planner — for conferences, trade shows, corporate summits, and government programs across APAC. No per-ticket platform fee. Transparent annual SGD pricing.",
    primaryCta: { label: "Start free trial", href: "/signup" },
    secondaryCta: { label: "Book a 15-min demo", href: "/contact?intent=demo" },
    tertiaryCta: { label: "See pricing", href: "#pricing" },
    stats: [
      { label: "Attendees managed", value: "30,000+", delta: "Enterprise programs across APAC" },
      { label: "Events delivered", value: "50+", delta: "Conferences, summits, expos" },
      { label: "Countries served", value: "7", delta: "Regional operations" },
      { label: "Platform uptime", value: "99.95%", delta: "Operational reliability" },
    ],
  },
  trust: {
    label: "Trusted by enterprise event teams across APAC",
    sponsors: ["THERME", "STRIDE", "NORTHWIND", "QUILL", "VERTEX", "ORBIT"],
    partners: [
      { name: "Therme", logoUrl: "", href: "" },
      { name: "Stride", logoUrl: "", href: "" },
      { name: "Northwind", logoUrl: "", href: "" },
    ],
    metrics: [
      { label: "Average queue reduction", value: "70%" },
      { label: "Typical deployment window", value: "48 hours" },
      { label: "Peak check-in throughput", value: "600+/hr" },
      { label: "Infrastructure region", value: "Singapore" },
    ],
  },
  problemSolution: {
    overline: "Why teams switch",
    title: "Your registration tool doesn't know what happened at the door.",
    painTitle: "Sound familiar?",
    pains: [
      "Registration lives in one system; check-in and badges in another — reconciliation waits until Monday.",
      "Marketing sends blasts blind to who actually showed up.",
      "Peak arrival hits and nobody has live gate visibility.",
      "Board asks for ROI; you're exporting CSVs from four vendors.",
    ],
    solutionTitle: "One operating system for the full lifecycle",
    solutions: [
      "Event registration software, email campaigns, QR check-in, and badges share one attendee record.",
      "Live dashboards during arrival — not post-event detective work.",
      "Hybrid event management with the same data model for in-person and virtual tracks.",
      "Annual SGD plans with clear caps — Studio at SGD 2,880/year with AI co-planner included.",
    ],
  },
  platform: {
    overline: "Platform modules",
    title: "Omnichannel event management software",
    titleLine2: "built for operators",
    description:
      "Registration, websites, onsite check-in, engagement, and reporting — with AivoryONE AI co-planner and transparent SGD annual pricing hosted in Singapore.",
    features: [
      {
        title: "Registration & Ticketing",
        body: "Launch branded registration flows and manage paid or free ticketing with clear controls.",
        outcome: "Increase conversion and reduce manual processing.",
        span: 4,
      },
      {
        title: "Event Websites & Landing Pages",
        body: "Publish event sites quickly with reusable structures and brand-safe customization.",
        outcome: "Go live faster with consistent digital experiences.",
        span: 4,
      },
      {
        title: "Onsite Check-In & Badge Printing",
        body: "Run fast check-in with QR scanning, instant validation, and print workflows.",
        outcome: "Reduce queues and improve arrival experience.",
        span: 4,
        accent: true,
      },
      {
        title: "AI Networking & Matchmaking",
        body: "Recommend people, sessions, and meetings based on profile and behavior signals.",
        outcome: "Improve attendee engagement and sponsor value.",
        span: 4,
      },
      {
        title: "Mobile Event Experience",
        body: "Deliver agendas, updates, and engagement tools in a mobile-first attendee journey.",
        outcome: "Increase session attendance and app adoption.",
        span: 4,
      },
      {
        title: "Analytics & Reporting",
        body: "Track registration funnels, attendance, engagement, and revenue by event and portfolio.",
        outcome: "Report performance faster with executive-ready insights.",
        span: 4,
      },
      {
        title: "Sponsor & Exhibitor Management",
        body: "Centralize deliverables, leads, and fulfillment tracking for sponsor teams.",
        outcome: "Protect sponsor outcomes and retention.",
        span: 4,
      },
      {
        title: "Hybrid & Virtual Events",
        body: "Coordinate in-person and online experiences from one conference management platform.",
        outcome: "Scale hybrid programs without operational complexity.",
        span: 4,
      },
    ],
  },
  eventTypes: {
    overline: "Solutions by program type",
    title: "Built for the events you actually run",
    items: [
      "Association conferences",
      "Corporate summits",
      "Trade shows & expos",
      "Government & public sector",
      "Hybrid & multi-track programs",
      "Training & certification events",
    ],
  },
  whyAivory: {
    overline: "Why Aivory EMS",
    title: "Designed for enterprise teams operating across APAC",
    points: [
      { title: "Hosted in Singapore", body: "APAC-first infrastructure with regional support and deployment expertise." },
      { title: "Enterprise flexibility", body: "Adaptable architecture for associations, agencies, and multi-brand event portfolios." },
      { title: "AI-powered workflows", body: "Automate repetitive decisions and surface real-time recommendations for operators." },
      { title: "48-hour deployment", body: "Launch production-ready programs quickly with proven templates and onboarding playbooks." },
      { title: "White-label capabilities", body: "Support sponsor or business-unit branding with controlled governance." },
      { title: "Operational reliability", body: "Status visibility, auditability, and platform safeguards for high-stakes event delivery." },
    ],
  },
  caseStudies: {
    overline: "Customer outcomes",
    title: "Results from Singapore & APAC event teams",
    items: [
      {
        customer: "Marina Bay FinTech Summit · Singapore",
        challenge: "Peak arrival queues across three entrances during a 2,400-delegate flagship program.",
        implementation: "Deployed Aivory QR check-in, badge printing, and live gate dashboards in 48 hours on Singapore infrastructure.",
        result: "Reduced registration queues by 70% during the opening hour.",
      },
      {
        customer: "Singapore Business Leaders Forum",
        challenge: "Board reporting required manual CSV merges from registration, email, and onsite vendors.",
        implementation: "Unified registration, communications, and analytics in one EMS workspace.",
        result: "Published post-event executive reports in under 24 hours.",
      },
      {
        customer: "APAC Cybersecurity Congress",
        challenge: "Hybrid tracks needed a single attendee record for in-person and virtual journeys.",
        implementation: "Coordinated onsite check-in and virtual session access from one operations console.",
        result: "Managed 10,000+ delegates with no major operational incident.",
      },
    ],
  },
  integrations: {
    overline: "Integrations",
    title: "Connect Aivory EMS to your existing stack",
    description: "Integrate CRM, marketing automation, payments, webinar, and analytics workflows to remove operational silos.",
    tools: ["HubSpot", "Stripe", "Zoom", "Google Analytics", "CRM systems", "Marketing automation", "Webhooks"],
  },
  pricing: {
    overline: "Pricing",
    title: "Annual plans in",
    titleMuted: "Singapore dollars.",
    plans: [
      {
        name: "Starter",
        price: "SGD 1,188",
        period: "/ year",
        body: "SMEs & associations · 5 events/yr · 500 attendees",
        cta: "Start free trial",
        ctaHref: "/signup?plan=starter",
        featured: false,
      },
      {
        name: "Studio",
        price: "SGD 2,880",
        period: "/ year",
        body: "Professional organizers · AI co-planner included",
        cta: "Start free trial",
        ctaHref: "/signup?plan=studio",
        featured: true,
      },
      {
        name: "Scale",
        price: "SGD 7,200",
        period: "/ year",
        body: "Agencies & conferences · API · webhooks · CSM",
        cta: "Start free trial",
        ctaHref: "/signup?plan=scale",
        featured: false,
      },
      {
        name: "Enterprise",
        price: "From SGD 14,880",
        period: "/ year",
        body: "Corporates & government · white-label · SSO · dedicated SLA",
        cta: "Contact sales",
        ctaHref: "/contact?intent=sales",
        featured: false,
      },
    ],
    deliveryModels: [
      { id: "self_serve", name: "Self-Serve", priceLabel: "Included", description: "Full admin console — you run events end-to-end." },
      { id: "hybrid", name: "Hybrid", priceLabel: "+ SGD 6,000/year", description: "Onboarding workshop, registration review, 20 support hours/year." },
      { id: "managed", name: "Managed", priceLabel: "+ SGD 15,000–50,000/year", description: "Done-for-you setup, journeys, badges, onsite coordination." },
    ],
    addOns: [
      { id: "attendees_5k", name: "Additional 5,000 attendees", priceSgd: 1000 },
      { id: "email_100k", name: "100,000 email credits", priceSgd: 500 },
      { id: "onsite_day", name: "Onsite support", priceSgd: 2000, period: "/day" },
    ],
  },
  cta: {
    title: "Ready to modernize",
    titleAccent: "your event operations?",
    body: "See how Aivory EMS supports conferences, corporate events, and large-scale experiences.",
    buttonLabel: "Book a demo",
    buttonHref: "/contact?intent=demo",
    secondaryLabel: "Talk to sales",
    secondaryHref: "/contact?intent=sales",
  },
  faq: {
    overline: "FAQ",
    title: "Event management software — answers for search & AI",
    items: [
      {
        q: "What is the best event management software in Singapore?",
        a: "For APAC teams that need registration, onsite check-in, badge printing, and reporting in one system, Aivory EMS is a strong choice: Singapore-hosted, annual SGD plans from 1,188, QR check-in and badges included on Starter (not a separate onsite add-on), and AivoryONE AI co-planner on Studio.",
      },
      {
        q: "What makes Aivory EMS different?",
        a: "Aivory EMS unifies registration, websites, onsite check-in, badge printing, campaigns, and AivoryONE AI co-planner in one platform — with annual SGD plans, bundled onsite tooling, and no per-ticket platform fee.",
      },
      {
        q: "How much does event registration software cost in Singapore?",
        a: "Aivory EMS Starter is SGD 1,188/year (5 events, 500 attendees/event). Studio is SGD 2,880/year with page builder and AivoryONE AI. Scale is SGD 7,200/year with API and webhooks. Enterprise from SGD 14,880/year.",
      },
      {
        q: "Does Aivory EMS include onsite check-in and badge printing?",
        a: "Yes — included from Starter. Run QR validation, live gate monitoring, and badge print workflows without renting separate onsite kiosk packages. Optional onsite operator support is available as a day-rate add-on.",
      },
      {
        q: "What is AivoryONE AI co-planner?",
        a: "AivoryONE is Aivory's governed event-execution agent: Understand → Analyse → Recommend → Execute → Audit. It sets up registration tiers, forms, landing pages, agenda, speakers, and EDM drafts from natural language — with human confirmation and audit logs — not recommendations alone.",
      },
      {
        q: "How does AivoryEMS compare to Cvent or GEVME?",
        a: "Cvent leads on AI RFP creation and Event Insights (Sep 2026). GEVME's Coplanner remains early-access content automation. AivoryEMS focuses on Singapore-hosted EMS with transparent SGD plans, bundled QR/badges from Starter, and AivoryONE as an in-workspace execution agent. See /compare for the matrix and EventOS procurement roadmap.",
      },
      {
        q: "Is Aivory EMS suitable for hybrid and virtual events?",
        a: "Yes. Manage in-person and online tracks, capacity, and attendee state from one console. Registration, communications, and analytics stay unified whether attendees join on-site or remotely.",
      },
      {
        q: "Do you charge per registration or per ticket?",
        a: "No per-ticket platform fee on subscription plans. You pay an annual SGD plan with clear event and attendee caps; registration payments can flow to your Stripe or Revolut account.",
      },
      {
        q: "Where is data hosted?",
        a: "Production runs in Singapore with APAC-first support — built for regional enterprise, association, and government event programs.",
      },
      {
        q: "How fast can we go live?",
        a: "Most teams launch within 48 hours using templates, AivoryONE setup workflows, Hybrid delivery onboarding, or managed setup for complex programs.",
      },
    ],
  },
  aivoryOne: {
    overline: "AivoryONE",
    title: "A governed event-execution agent",
    titleMuted: "not just a content copilot.",
    body: "Designed around Understand → Analyse → Recommend → Execute → Audit — so AI can operate registration, communications, and analytics inside your workspace with confirmation and audit trails.",
    stages: [
      { title: "Understand", body: "Parse briefs and intents in your tenant context." },
      { title: "Analyse", body: "Inspect setup gaps and registration signals." },
      { title: "Recommend", body: "Propose playbooks with human checkpoints." },
      { title: "Execute", body: "Run in-product tools for events, forms, pages, and campaigns." },
      { title: "Audit", body: "Log AI mutations for operators and security reviews." },
    ],
    cta: { label: "See competitive positioning", href: "/compare" },
  },
  seo: {
    title: "Event Management Software Singapore | Registration, Check-in & AI | Aivory EMS",
    description:
      "Singapore-hosted event management software: registration, QR check-in, badges, campaigns & AivoryONE AI co-planner. From SGD 1,188/yr. No per-ticket fee.",
  },
  footer: {
    copyright: "© 2026 AIVORY LABS · HOSTED IN SINGAPORE",
    links: [
      { label: "Status", href: "/status" },
      { label: "Trust Centre", href: "/trust" },
      { label: "Security", href: "/security" },
      { label: "Privacy", href: "/privacy" },
      { label: "Terms", href: "/terms" },
    ],
  },
};
